import { LoadingOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Spin,
  Switch,
} from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { GetActiveScalesApi } from "../../../api/GetActiveScalesApi";
import GetScaleUnitsByScaleApi from "../../../api/GetScaleUnitsByScaleApi";
import GetPlanTemplateByIdApi from "../../../api/plan-templates/GetPlanTemplateByIdApi";
import EditSplitPlanApi from "../../../api/plans/EditSplitPlanApi";
import moment from "moment";
import { GetUnitPositionsApi2 } from "../../../api/units/GetUnitPositionsApi2";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../../helpers/NotificationHelpers";
import { GetEvaluationsApi } from "../../../api/ehrm/GetEvaluationsApi";
import { GetEmployeeEvaluationsApi } from "../../../api/ehrm/GetEmployeeEvaluationsApi";
import { GetGoalGroupsApi } from "../../../api/ehrm/GetGoalGroupsApi";
import PhrmService from "../../../services/PhrmService";

const { Option } = Select;

const { TextArea } = Input;

function EditSplitPlanModal(props) {
  const [submit, setSubmit] = useState(false);
  const [planLevels, setPlanLevels] = useState([]);
  const [planLevel, setPlanLevel] = useState([]);
  const [scales, setScales] = useState([]);
  const [scaleUnits, setScaleUnits] = useState([]);
  const [percentage, setPercentage] = useState(false);
  const [addScaleUnit, setAddScaleUnit] = useState(false);
  const [unitPositions, setUnitPositions] = useState([]);
  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const [form] = Form.useForm();
  const [valuesChanged, setValuesChanged] = useState(false);
  const [freshPlanData, setFreshPlanData] = useState(null);

  function handleCancel() {
    setSubmit(false);
    setPlanLevels([]);
    setPlanLevel([]);
    setScales([]);
    setScaleUnits([]);
    setUnitPositions([]);
    setPercentage(false);
    setAddScaleUnit(false);
    form.resetFields();
    props.handleCancel();
    setValuesChanged(false);
  }

  useEffect(() => {
    if (props.visible) {
      getActiveScales();
      getPlanLevels();
      getUnitUsers();
      getFreshPlanData();
    }
  }, [props.visible]);

  async function getFreshPlanData() {
    if (!props.plan?.planId) return;
    
    try {
      const token = 'Bearer ' + localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}v1/plans/get-plan-details`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({
            id: props.plan.planId,
            positionId: props.plan?.assignedTo?.positionId || null,
          }),
        }
      );
      
      const apiResponse = await response.json();
      
      // Handle different response structures for split plans vs top plans
      let planData = null;
      if (apiResponse.success && apiResponse.data) {
        // Top plan structure
        planData = apiResponse.data;
      } else if (apiResponse.message === 'success' && apiResponse.plan) {
        // Split plan structure
        planData = apiResponse.plan;
      }
      
      if (planData) {
        setFreshPlanData(planData);
      }
    } catch (error) {
      console.error('Error fetching fresh plan data:', error);
    }
  }

  useEffect(() => {
    if (planLevels && props.plan) {
      selectPlanLevel(props.plan?.planLevelId);

      // Initialize scale and load scale units if needed
      if (props.plan?.scale?.scaleId) {
        selectScale(props.plan.scale.scaleId);
      }

      // Set basic form fields with plan data
      const formFields = [
        {
          name: "targetScale",
          value: props.plan?.scale?.scaleId,
        },
        {
          name: "unitScale",
          value: props.plan?.scale?.scaleUnitId,
        },
        {
          name: "target",
          value: props.plan?.target,
        },
        {
          name: "description",
          value: props.plan?.description,
        },
        {
          name: "targetDate",
          value: props.plan?.targetDate ? moment(props.plan.targetDate) : null,
        },
        {
          name: "cascadeFromSplitPlans",
          value: Boolean(props.plan?.cascadeFromSplitPlans) || props.plan?.cascadeFromSplitPlans === 1 || props.plan?.cascadeFromSplitPlans === '1',
        },
      ];

      form.setFields(formFields);
    }
  }, [planLevels, props.plan]);

  // Set form values for attributes after component loads - copied from EditTopPlanModal
  useEffect(() => {
    if (freshPlanData?.planAttributeValue && planLevel?.planLevelAttributes) {
      // Wait for dropdown options to be fully loaded
      setTimeout(() => {
        const formFields = [];
        
        freshPlanData.planAttributeValue.forEach((attribute) => {
          let fieldValue = "";
          
          // Find the template attribute to get the valueType and dropdown options
          const templateAttr = planLevel.planLevelAttributes.find(
            (attr) => attr.id === attribute.planAttributeId
          );
          
          if (!templateAttr) {
            return; // Skip if template attribute not found
          }
          
          // Initialize dropdown fields with empty array by default
          if (templateAttr.valueType === "dropdown") {
            fieldValue = [];
          }
          
          if (attribute.value !== null && attribute.value !== undefined) {
            if (templateAttr.valueType === "text") {
              fieldValue = attribute.text || "";
            } else if (templateAttr.valueType === "number") {
              fieldValue = attribute.number;
            } else if (templateAttr.valueType === "double") {
              fieldValue = parseFloat(attribute.double);
            } else if (templateAttr.valueType === "date") {
              fieldValue = attribute.date ? moment(attribute.date) : null;
            } else if (templateAttr.valueType === "dropdown") {
              // Backend now always returns normalized JSON array string
              let rawDropdownValue = attribute.dropdown || attribute.value;
              
              try {
                if (rawDropdownValue && typeof rawDropdownValue === 'string') {
                  // Parse the JSON array string from backend
                  const parsed = JSON.parse(rawDropdownValue);
                  
                  if (Array.isArray(parsed)) {
                    fieldValue = parsed;
                  } else {
                    fieldValue = [];
                  }
                } else {
                  fieldValue = [];
                }
                
                // Validate that the values exist in the available options
                const availableOptionIds = templateAttr.dropdownOptions?.map(opt => opt.id) || [];
                const validValues = fieldValue.filter(val => availableOptionIds.includes(val));
                
                // Use valid values only
                fieldValue = validValues;
              } catch (e) {
                console.error('Error parsing dropdown value:', e);
                fieldValue = [];
              }
            } else if (templateAttr.valueType === "boolean") {
              fieldValue = Boolean(attribute.value);
            }
          } else {
            // Handle null/undefined values for specific types
            if (templateAttr.valueType === "dropdown") {
              fieldValue = [];
            }
          }
          
          const fieldName = templateAttr.id;
          
          formFields.push({
            name: fieldName,
            value: fieldValue,
          });
        });
        
        // Set all form fields at once
        if (formFields.length > 0) {
          form.setFields(formFields);
        }
      }, 1000); // Timeout to ensure dropdown options are loaded
    }
  }, [freshPlanData?.planAttributeValue, planLevel?.planLevelAttributes]);

  async function getUnitUsers() {
    const apiResponse = await GetUnitPositionsApi2(props.plan?.unit?.unitId);
    if (apiResponse.message === "success") {
      setUnitPositions(apiResponse.data);

      form.setFields([
        {
          name: "assignTo",
          value: props.plan?.assignedTo?.positionId,
        },
      ]);
    }
  }

  async function onFinish(values) {
    setSubmit(true);

    let attributesValues = [];
    planLevel?.planLevelAttributes?.forEach((attribute) => {
      let attributeValue = values[attribute.id];
      
      // Handle dropdown values - skip empty arrays
      if (attribute.valueType === "dropdown") {
        if (Array.isArray(attributeValue) && attributeValue.length === 0) {
          // Skip empty dropdown selections
          return;
        }
      }
      
      // Skip undefined or null values for optional attributes
      if (attributeValue === undefined || attributeValue === null || attributeValue === "") {
        return;
      }
      
      attributesValues.push({
        attributeId: attribute.id,
        value: attributeValue,
      });
    });

    const apiResponse = await EditSplitPlanApi(
      props.plan?.planId,
      values.title,
      values.description || "",
      values.target,
      values.targetDate || "",
      values.assignTo,
      attributesValues,
      values.targetScale,
      values.unitScale,
      values.cascadeFromSplitPlans
    );
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.message === "success") {
      openNotificationSuccess("bottomRight", `Split plan edited successfully`);
      props.handleOk(apiResponse.data);
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't split plan. Try again later."
      );
    }
    handleCancel();
  }

  async function getPlanLevels() {
    let newPlanLevels = [];
    const apiResponse = await GetPlanTemplateByIdApi(perfCycleState?.template);
    if (apiResponse.success) {
      apiResponse.data?.planLevels?.forEach((level) => {
        if (level.order !== 1) {
          newPlanLevels.push(level);
        }
      });
      setPlanLevels(newPlanLevels);
    }
  }

  async function getActiveScales() {
    const apiResponse = await GetActiveScalesApi();
    if (apiResponse.success) {
      setScales(apiResponse.data);
    }
  }

  async function selectScale(value) {
    setScaleUnits([]);
    let addUnit = "";
    scales.forEach((scale) => {
      if (scale.id === value) {
        if (scale.name === "Number") {
          setAddScaleUnit(false);
          setPercentage(false);
        } else if (scale.name === "Percentage") {
          setAddScaleUnit(false);
          setPercentage(true);
        } else {
          setAddScaleUnit(true);
          setPercentage(false);
          addUnit = "add";
        }
      }
    });
    if (addUnit === "add") {
      const apiResponse = await GetScaleUnitsByScaleApi(value);
      if (apiResponse.message === "success") {
        setScaleUnits(apiResponse.scaleUnits);
      }
      apiResponse.scaleUnits?.forEach((unit) => {
        if (unit.default) {
          form.setFields([
            {
              name: "unitScale",
              value: unit.id,
            },
          ]);
        }
      });
    }
  }
  async function selectPlanLevel(value) {
    planLevels?.forEach((level) => {
      if (level.id === value) {
        setPlanLevel(level);
      }
    });
  }


  const unitSelector = (
    <Form.Item name="unitScale" noStyle>
      <Select style={{ width: 100 }}>
        {scaleUnits.map((unit) => {
          return (
            <Option key={unit.id} value={unit.id}>
              {unit.abbreviation}
            </Option>
          );
        })}
      </Select>
    </Form.Item>
  );

  function renderDropdownAndBooleanAttributes() {
    let output = [];

    const dropdownAndBooleanAttributes = planLevel?.planLevelAttributes?.filter((attribute) => {
      return attribute.valueType === "dropdown" || attribute.valueType === "boolean";
    }) || [];

    dropdownAndBooleanAttributes.forEach((attribute) => {
      if (attribute.valueType === "dropdown") {
        output.push(
          <Col span={12} key={attribute.id}>
            <Form.Item 
              label={attribute.name} 
              name={attribute.id}
              initialValue={[]}
            >
              <Select
                mode="multiple"
                showSearch
                placeholder={`Select ${attribute.name}`}
                style={{ width: "100%", maxWidth: "300px" }}
                getPopupContainer={(trigger) => trigger.parentNode}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                optionLabelProp="children"
              >
                {attribute.dropdownOptions?.map((option) => (
                  <Option key={option.id} value={option.id}>
                    {option.value}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        );
      } else if (attribute.valueType === "boolean") {
        output.push(
          <Col span={12} key={attribute.id}>
            <Form.Item 
              label={attribute.name} 
              name={attribute.id}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
        );
      }
    });

    return output;
  }

  function renderOtherAttributes() {
    let output = [];

    const otherAttributes = planLevel?.planLevelAttributes?.filter((attribute) => {
      return !(attribute.valueType === "dropdown" || attribute.valueType === "boolean");
    }) || [];

    otherAttributes.forEach((attribute) => {
      if (attribute.valueType === "text" || attribute.valueType === "number" || attribute.valueType === "double" || attribute.valueType === "date") {
        output.push(
          <Col span={12} key={attribute.id}>
            <Form.Item 
              label={attribute.name} 
              name={attribute.id}
              rules={[
                {
                  validator: (_, value) => {
                    if (attribute.valueType === "double" && value !== undefined && value !== null) {
                      if (attribute.minValue !== null && value < attribute.minValue) {
                        return Promise.reject(`Minimum value is ${attribute.minValue}`);
                      }
                      if (attribute.maxValue !== null && value > attribute.maxValue) {
                        return Promise.reject(`Maximum value is ${attribute.maxValue}`);
                      }
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              {attribute.valueType === "text" ? (
                <Input style={{ width: "100%" }} placeholder={attribute.name} />
              ) : attribute.valueType === "number" ? (
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  placeholder={attribute.name}
                />
              ) : attribute.valueType === "double" ? (
                <InputNumber
                  min={attribute.minValue || 0}
                  max={attribute.maxValue || undefined}
                  step={1 / Math.pow(10, attribute.decimalPrecision || 2)}
                  precision={attribute.decimalPrecision || 2}
                  style={{ width: "100%" }}
                  placeholder={attribute.name}
                  formatter={(value) => {
                    if (!value) return '';
                    const precision = attribute.decimalPrecision || 2;
                    return parseFloat(value).toFixed(precision);
                  }}
                  parser={(value) => {
                    if (!value) return '';
                    return parseFloat(value.replace(/[^\d.-]/g, ''));
                  }}
                />
              ) : attribute.valueType === "date" ? (
                <DatePicker style={{ width: "100%" }} placeholder={attribute.name} />
              ) : null}
            </Form.Item>
          </Col>
        );
      }
    });

    return output;
  }

  return (
    <>
      <Modal
        title={`Edit split Plan`}
        visible={props.visible}
        onCancel={() => handleCancel()}
        footer={null}
        destroyOnClose={true}
        width={900}
      >
        <Form
          name="splitPlan"
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
          requiredMark={false}
          form={form}
          initialValues={{
            planGroup: props.plan?.planGroup,
            planLevel: props.plan?.planLevel,
            title: props.plan?.title,
          }}
          onValuesChange={() => {
            setValuesChanged(true);
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Plan Group*" name="planGroup">
                <Input disabled placeholder="Plan group" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="Plan Level" name="planLevel">
                <Input disabled placeholder="Plan level" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={`Title*`}
                name="title"
                rules={[
                  {
                    required: true,
                    message: `Please enter title!`,
                  },
                ]}
              >
                <Input placeholder={"title"} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Assign to"
                name="assignTo"
                rules={[
                  {
                    required: true,
                    message: "Please select a user!",
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select user"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  filterSort={(optionA, optionB) =>
                    optionA.children
                      .toLowerCase()
                      .localeCompare(optionB.children.toLowerCase())
                  }
                >
                  {unitPositions.map((position) => {
                    return (
                      <Option key={position.id} value={position.id}>
                        {(position.user?.firstName
                          ? position.user?.firstName + ", "
                          : "") + position.designation?.name}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>

            {planLevel.target && (
              <>
                <Col span={12}>
                  <Form.Item
                    label="Target scale*"
                    name="targetScale"
                    rules={[
                      {
                        required: true,
                        message: "Please select a target scale!",
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      placeholder="Select a scale"
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                      filterSort={(optionA, optionB) =>
                        optionA.children
                          .toLowerCase()
                          .localeCompare(optionB.children.toLowerCase())
                      }
                      onChange={(value) => selectScale(value)}
                    >
                      {scales.map((scale) => {
                        return (
                          <Option key={scale.id} value={scale.id}>
                            {scale.name}
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label="Target*"
                    name="target"
                    rules={[
                      {
                        required: true,
                        message: "Please enter target!",
                      },
                    ]}
                  >
                    <InputNumber
                      min={0}
                      addonAfter={
                        addScaleUnit ? unitSelector : percentage && "%"
                      }
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              </>
            )}

            {planLevel.description && (
              <Col span={24}>
                <Form.Item label="Description" name="description">
                  <TextArea
                    showCount
                    maxLength={100}
                    placeholder="Description"
                  />
                </Form.Item>
              </Col>
            )}


            {planLevel.targetDate && (
              <Col span={12}>
                <Form.Item label="Target date*" name="targetDate">
                  <DatePicker className="w-full" placeholder="Target date" />
                </Form.Item>
              </Col>
            )}

            {planLevel.cascadeFromSplitPlansEnabled && (
              <Col span={24}>
                <Form.Item 
                  label="Cascade from Split Plans"
                  name="cascadeFromSplitPlans"
                  tooltip="When enabled, split plans below this plan will roll up their progress. Individual progress on this plan will be ignored and update button will be disabled."
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            )}

            {renderDropdownAndBooleanAttributes()}
            {renderOtherAttributes()}

            <Col span={24}>
              <Form.Item style={{ marginBottom: 0 }}>
                <div className=" flex justify-end">
                  <Button className="mr-3 w-28" onClick={() => handleCancel()}>
                    Cancel
                  </Button>
                  <Button
                    className="btn w-32"
                    type="primary"
                    htmlType={submit && "submit"}
                    disabled={submit || !valuesChanged}
                  >
                    {submit ? (
                      <Spin
                        size="small"
                        indicator={
                          <LoadingOutlined style={{ color: "#fff" }} spin />
                        }
                      />
                    ) : (
                      `Save`
                    )}
                  </Button>
                </div>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}

export default EditSplitPlanModal;
